
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FormLabel } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useStorage } from '@/hooks/use-storage';
import { isValidUUID } from '@/lib/utils/uuid';
import { generateUUID } from '@/lib/utils/uuid';

interface LogoUploaderProps {
  companyId: string;
  initialLogo: string | null;
  onLogoChange: (logoUrl: string | null) => void;
}

export function LogoUploader({ companyId, initialLogo, onLogoChange }: LogoUploaderProps) {
  const [previewLogo, setPreviewLogo] = useState<string | null>(initialLogo);
  const { uploadFile, isUploading, deleteFile } = useStorage();
  
  // Initialize with localStorage value if in dev mode
  useEffect(() => {
    if (import.meta.env.DEV) {
      const storageKey = `logo_url_${companyId}`;
      const savedLogo = localStorage.getItem(storageKey);
      if (savedLogo && (!initialLogo || initialLogo !== savedLogo)) {
        setPreviewLogo(savedLogo);
        onLogoChange(savedLogo);
      }
    }
  }, [companyId, initialLogo, onLogoChange]);
  
  // Manejar la subida de un nuevo logo
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validar tipo de archivo
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Tipo de archivo no permitido. Por favor usa JPEG, PNG, GIF o SVG.');
      return;
    }
    
    // Validar tamaño máximo (5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB en bytes
    if (file.size > maxSize) {
      toast.error('El archivo es demasiado grande. El tamaño máximo es 5MB.');
      return;
    }
    
    try {
      // Crear vista previa inmediata para mejor UX
      const objectUrl = URL.createObjectURL(file);
      setPreviewLogo(objectUrl);
      
      // Verificamos si estamos en modo desarrollo o con un ID no válido
      if (!isValidUUID(companyId)) {
        console.log(`Modo desarrollo: Simulando carga de logo para empresa "${companyId}"`);
        
        // En modo desarrollo, simulamos la carga exitosa
        // Guardar URL en localStorage para persistencia
        if (typeof window !== 'undefined') {
          const storageKey = `logo_url_${companyId}`;
          window.localStorage.setItem(storageKey, objectUrl);
        }
        
        onLogoChange(objectUrl); // Usamos la URL temporal como logoUrl
        toast.success('Logo cargado en modo desarrollo');
        return;
      }
      
      // Subir el archivo usando nuestro hook personalizado
      const pathToStore = `company-${companyId}/logo-${generateUUID()}-${file.name}`;
      const fileUrl = await uploadFile(pathToStore, file);
      
      onLogoChange(fileUrl);
      toast.success('Logo cargado con éxito');
    } catch (error: any) {
      console.error('Error al subir el logo:', error);
      toast.error(`Error al subir el logo: ${error.message || 'Error desconocido'}`);
      setPreviewLogo(null); // Limpiar vista previa en caso de error
    }
  };
  
  // Eliminar el logo actual
  const handleRemoveLogo = async () => {
    // Si tenemos una vista previa temporal, simplemente la limpiamos
    if (previewLogo && previewLogo.startsWith('blob:')) {
      URL.revokeObjectURL(previewLogo);
      setPreviewLogo(null);
      onLogoChange(null);
      
      // Eliminar del localStorage en modo desarrollo
      if (import.meta.env.DEV) {
        const storageKey = `logo_url_${companyId}`;
        localStorage.removeItem(storageKey);
      }
      
      return;
    }
    
    // Si tenemos una URL real y no es modo desarrollo, intentamos eliminarla
    if (initialLogo && !initialLogo.startsWith('blob:') && isValidUUID(companyId)) {
      try {
        // Extraer la ruta del archivo de la URL
        const path = initialLogo.split('/').pop() || '';
        if (path) {
          await deleteFile(path);
        }
      } catch (error) {
        console.error('Error al eliminar logo:', error);
      }
    }
    
    // Limpiar en cualquier caso
    setPreviewLogo(null);
    onLogoChange(null);
    
    // Eliminar del localStorage en modo desarrollo
    if (import.meta.env.DEV) {
      const storageKey = `logo_url_${companyId}`;
      localStorage.removeItem(storageKey);
    }
    
    toast.success('Logo eliminado');
  };
  
  // Handle direct file selection with click on the button
  const handleSelectFileClick = () => {
    // Find the hidden file input and trigger its click event
    const fileInput = document.getElementById('logo-file-input');
    if (fileInput) {
      fileInput.click();
    }
  };
  
  // Manejar eventos de arrastrar y soltar
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (file.type.startsWith('image/')) {
        // Usamos la misma función de upload que para la selección manual
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.files = e.dataTransfer.files;
        handleLogoUpload({ target: fileInput } as any);
      } else {
        toast.error('Por favor, sube solo archivos de imagen');
      }
    }
  };
  
  return (
    <div className="space-y-4">
      <FormLabel>Logo de la empresa</FormLabel>
      <Card 
        className="p-6 border-dashed border-2 flex flex-col items-center justify-center space-y-4"
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {previewLogo ? (
          <div className="relative">
            <img 
              src={previewLogo} 
              alt="Logo de empresa" 
              className="w-60 h-auto object-contain" 
            />
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute -top-3 -right-3 rounded-full"
              onClick={handleRemoveLogo}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <>
            <div className="p-4 rounded-full bg-muted">
              <Upload className="h-10 w-10 text-primary" />
            </div>
            <div className="text-center">
              <p className="font-medium">Arrastra y suelta o haz clic para subir</p>
              <p className="text-sm text-muted-foreground">
                PNG, JPG, GIF o SVG (máx. 5MB)
              </p>
            </div>
          </>
        )}
        
        <Input
          id="logo-file-input"
          type="file"
          accept="image/png,image/jpeg,image/gif,image/svg+xml"
          className="hidden"
          onChange={handleLogoUpload}
          disabled={isUploading}
        />
        
        {!previewLogo && (
          <Button 
            type="button" 
            disabled={isUploading}
            variant="outline"
            onClick={handleSelectFileClick}
          >
            {isUploading ? 'Subiendo...' : 'Seleccionar archivo'}
          </Button>
        )}
      </Card>
    </div>
  );
}
